import React, { useEffect, useMemo, useState } from "react";
import { useAuthContext } from "../../hooks/useAuthContext";
import { msmartAxios } from "../../api/axios";
import { Link, useParams } from "react-router-dom";
import "flatpickr/dist/themes/material_green.css";
import Flatpickr from "react-flatpickr";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import SalesTartget from "./SalesTarget";
import { useToast } from "../../context/ToastContext";
import SummaryCharts, { RangePresets, SummaryNavItems, useSummaryTab } from "./components/summary/SummaryCharts";
import useTeamAccess from "./hooks/useTeamAccess";
import { SummaryBodySkeleton } from "./components/MsmartSkeletons";
import ErrorState from "../../components/ErrorState";
import { getErrorMessage } from "../../utils/errorMessage";


// =========================
// UI helpers
// =========================
function SectionCard({ title, right, children }) {
  return (
    <div className="card p-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="fw-bold">{title}</div>
        {right ? <div>{right}</div> : null}
      </div>
      {children}
    </div>
  );
}

function MetricRow({ label, value, suffix = "", tone = "text-dark" }) {
  return (
    <div className="d-flex justify-content-between align-items-center py-2 border-top">
      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
        {label}
      </div>
      <div className={`fw-bold ${tone}`}>
        {value}
        {suffix}
      </div>
    </div>
  );
}

function KpiCard({ icon, title, value, tone = "text-dark", sub, onClick }) {
  return (
    <button
      type="button"
      className="card ss-kpi text-center p-3 h-100 w-100"
      onClick={onClick}
      style={{ textAlign: "inherit" }}
    >
      <div className="d-flex justify-content-center mb-2">
        <div className="ss-kpi-icon">
          <i className={`bi ${icon}`} style={{ fontSize: "1.4rem" }} />
        </div>
      </div>

      <div className={`fw-bold ${tone}`} style={{ fontSize: "1.25rem" }}>
        {value}
      </div>

      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
        {title}
      </div>

      {sub ? (
        <div className="text-muted mt-1" style={{ fontSize: "0.8rem" }}>
          {sub}
        </div>
      ) : null}
    </button>
  );
}

// Helper: Generate all dates in range (YYYY-MM-DD)
function getDateRangeList(start, end) {
  const result = [];
  const current = new Date(start);
  const last = new Date(end);

  while (current <= last) {
    const formatted = current.toLocaleDateString("sv-SE");
    result.push(formatted);
    current.setDate(current.getDate() + 1);
  }
  return result;
}

function money(n) {
  const num = Number(n || 0);
  return `RM ${num.toLocaleString("en-MY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Top days helper
function calcTopDays(countByDate = {}, topN = 3) {
  return Object.entries(countByDate || {})
    .map(([d, v]) => [d, Number(v || 0)])
    .filter((x) => x[1] > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN);
}

export default function StatSummary() {
  const { user } = useAuthContext();
  const { teamId } = useParams();
  const { notifyError } = useToast();
  const [tab, setTab] = useSummaryTab(); // "summary" | "analysis" (?view=analysis)

  const [summary, setSummary] = useState({
    totalLeadsAdded: { total: 0, countByDate: {} },
    totalPresentations: { total: 0, countByDate: {} },
    followUpScheduled: { total: 0, countByDate: {} },
    finalStatus: {
      Booking: { total: 0, countByDate: {} },
      Closed: { total: 0, countByDate: {} },
      Rejected: { total: 0, countByDate: {} },
      "Follow Up": { total: 0, countByDate: {} },
    },
    avgFollowUpPerLead: { total: 0 },
    followUpPerCloseRatio: { total: 0 },
    followUpToClosedRate: { total: 0, totalFollowUpLeads: 0, totalClosedLeads: 0 },
    followUpToRejectedRate: { total: 0, totalFollowUpLeads: 0, totalRejectedLeads: 0 },
    followUpToBookingRate: { total: 0, totalFollowUpLeads: 0, totalBookingLeads: 0 },
    bookingToClosedRate: { total: 0, totalBookingLeads: 0, totalClosedFromBooking: 0 },
    bookingToRejectedRate: { total: 0, totalBookingLeads: 0, totalRejectedFromBooking: 0 },
    salesGapSummary: {
      targetAmount: 0.0,
      actualSalesAmount: 0,
      salesGap: 0.0,
      percentageAchieved: 0,
      countByDate: {},
    },
  });

  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState([new Date(), new Date()]);

  // KPI detail modal state
  const [kpiModal, setKpiModal] = useState({
    open: false,
    title: "",
    subtitle: "",
    tone: "text-dark",
    icon: "bi-graph-up",
    total: 0,
    extraRows: [],
    countByDate: null,
    isMoney: false,
  });

  const openKpiModal = (payload) => {
    setKpiModal({ open: true, ...payload });
  };
  const closeKpiModal = () => {
    setKpiModal((p) => ({ ...p, open: false }));
  };

  const daysInRange = useMemo(() => {
    if (!filterDate?.[0] || !filterDate?.[1]) return 1;
    const a = new Date(filterDate[0]);
    const b = new Date(filterDate[1]);
    const diff = Math.floor((b - a) / (1000 * 60 * 60 * 24)) + 1;
    return diff <= 0 ? 1 : diff;
  }, [filterDate]);

  const rangeLabel = useMemo(() => {
    if (!filterDate?.[0] || !filterDate?.[1]) return "";
    const a = `${filterDate[0]}`.slice(4, 15);
    const b = `${filterDate[1]}`.slice(4, 15);
    return `${a} to ${b}`;
  }, [filterDate]);

  // =====================
  // Gate: ensure user belongs to team
  // =====================
  const access = useTeamAccess(teamId);
  const view = access.view;

  // =====================
  // Fetch data on date change
  // =====================
  useEffect(() => {
    if (!filterDate?.length) return;

    const start = filterDate[0];
    const end = filterDate.length === 1 ? filterDate[0] : filterDate[1];

    const formattedStart = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(
      start.getDate()
    ).padStart(2, "0")}`;
    const formattedEnd = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(
      end.getDate()
    ).padStart(2, "0")}`;

    fetchData({ startDate: formattedStart, endDate: formattedEnd });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDate]);

  const fetchData = async ({ startDate, endDate }) => {
    setLoading(true);
    try {
      const response = await msmartAxios.post(
        `/api/msmart/stats/individual/${teamId}`,
        { startDate, endOfDate: endDate },
        { headers: { accessToken: user.token } }
      );
      setSummary(response.data);
    } catch (err) {
      notifyError(getErrorMessage(err, "Unable to fetch summary."));
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (selectedDates) => {
    if (selectedDates?.length === 2) setFilterDate(selectedDates);
  };

  // =====================
  // Excel export
  // =====================
  const exportToExcel = async (data, startDate, endDate) => {
    const workbook = new ExcelJS.Workbook();

    const summarySheet = workbook.addWorksheet("Summary");
    summarySheet.addRow(["Indicator", "Total", "Details"]);
    summarySheet.addRow(["Total Leads Added", data.totalLeadsAdded.total, ""]);
    summarySheet.addRow(["Total Presentations", data.totalPresentations.total, ""]);
    summarySheet.addRow(["Follow-ups Done", data.followUpScheduled.total, ""]);

    const statuses = ["Booking", "Closed", "Rejected", "Follow Up"];
    statuses.forEach((status) => {
      const stat = data.finalStatus[status] || {};
      summarySheet.addRow([`${status}`, stat.total, ""]);
    });

    summarySheet.addRow(["Avg Follow Up / Lead", data.avgFollowUpPerLead.total, ""]);
    summarySheet.addRow(["Avg Follow Up / Closed Lead", data.followUpPerCloseRatio.total, ""]);

    summarySheet.addRow([
      "Follow Up ➝ Closed Rate",
      `${data.followUpToClosedRate.total}%`,
      `Follow Up: ${data.followUpToClosedRate.totalFollowUpLeads}, Closed: ${data.followUpToClosedRate.totalClosedLeads}`,
    ]);

    summarySheet.addRow([
      "Follow Up ➝ Rejected Rate",
      `${data.followUpToRejectedRate.total}%`,
      `Follow Up: ${data.followUpToRejectedRate.totalFollowUpLeads}, Rejected: ${data.followUpToRejectedRate.totalRejectedLeads}`,
    ]);

    summarySheet.addRow([
      "Follow Up ➝ Booking Rate",
      `${data.followUpToBookingRate.total}%`,
      `Follow Up: ${data.followUpToBookingRate.totalFollowUpLeads}, Booking: ${data.followUpToBookingRate.totalBookingLeads}`,
    ]);

    summarySheet.addRow([
      "Booking ➝ Closed Rate",
      `${data.bookingToClosedRate.total}%`,
      `Booking: ${data.bookingToClosedRate.totalBookingLeads}, Closed: ${data.bookingToClosedRate.totalClosedFromBooking}`,
    ]);

    summarySheet.addRow([
      "Booking ➝ Rejected Rate",
      `${data.bookingToRejectedRate.total}%`,
      `Booking: ${data.bookingToRejectedRate.totalBookingLeads}, Rejected: ${data.bookingToRejectedRate.totalRejectedFromBooking}`,
    ]);

    summarySheet.addRow(["Total Targeted Sales", data.salesGapSummary.targetAmount, ""]);
    summarySheet.addRow(["Total Actual Sales", data.salesGapSummary.actualSalesAmount, ""]);
    summarySheet.addRow(["Total Sales Gap", data.salesGapSummary.salesGap * -1, ""]);

    const countSheet = workbook.addWorksheet("Summary By Date");
    const headerRow = [
      "Date",
      "Leads Added",
      "Presentation",
      "Follow-ups Done",
      "Closed",
      "Rejected",
      "Booking",
      "Targeted Sales",
      "Actual Sales",
      "Sales Gap",
    ];
    countSheet.addRow(headerRow);

    const dateList = getDateRangeList(startDate, endDate);
    dateList.forEach((date) => {
      countSheet.addRow([
        date,
        data.totalLeadsAdded.countByDate?.[date] || 0,
        data.totalPresentations.countByDate?.[date] || 0,
        data.followUpScheduled.countByDate?.[date] || 0,
        data.finalStatus.Closed.countByDate?.[date] || 0,
        data.finalStatus.Rejected.countByDate?.[date] || 0,
        data.finalStatus.Booking.countByDate?.[date] || 0,
        data.salesGapSummary.countByDate?.[date]?.targetAmount || 0,
        data.salesGapSummary.countByDate?.[date]?.actualSalesAmount || 0,
        (data.salesGapSummary.countByDate?.[date]?.salesGap || 0) * -1,
      ]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const username = user.username;

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const shortStartDate = `${startDate}`.slice(4, 15).split("-").reverse().join();
    const shortEndDate = `${endDate}`.slice(4, 15).split("-").reverse().join();

    saveAs(blob, `Msmart Summary (${username}) ${shortStartDate} to ${shortEndDate}.xlsx`);
  };

  // =====================
  // UI computed values
  // =====================
  const targeted = summary?.salesGapSummary?.targetAmount || 0;
  const actual = summary?.salesGapSummary?.actualSalesAmount || 0;
  const gap = (summary?.salesGapSummary?.salesGap || 0) * -1;
  const achieved = Number(summary?.salesGapSummary?.percentageAchieved || 0);

  const gapTone = achieved >= 95 ? "text-success" : "text-danger";
  const actualTone = actual >= targeted ? "text-success" : "text-danger";

  // Build money series from salesGapSummary.countByDate
  const salesTargetByDate = useMemo(() => {
    const obj = {};
    const source = summary?.salesGapSummary?.countByDate || {};
    Object.keys(source).forEach((d) => {
      obj[d] = source?.[d]?.targetAmount || 0;
    });
    return obj;
  }, [summary]);

  const salesActualByDate = useMemo(() => {
    const obj = {};
    const source = summary?.salesGapSummary?.countByDate || {};
    Object.keys(source).forEach((d) => {
      obj[d] = source?.[d]?.actualSalesAmount || 0;
    });
    return obj;
  }, [summary]);

  const salesGapByDate = useMemo(() => {
    const obj = {};
    const source = summary?.salesGapSummary?.countByDate || {};
    Object.keys(source).forEach((d) => {
      obj[d] = (source?.[d]?.salesGap || 0) * -1;
    });
    return obj;
  }, [summary]);

  return (
    <div className="ss-page">
      <div className="container py-6">
        {/* Header */}
        <div className="row justify-content-center text-start">
          <div className="col-lg-12">
          <div>
            <h1 className="header-title mb-1">M-Smart</h1>
            <div className="text-muted" style={{ fontSize: 14 }}>
            Sales Activity Management.
            </div>
          </div>
          </div>
        </div>

        <div className="card text-center mt-4">
          {/* Tabs */}
          <div className="card-header text-start">
            <ul className="nav nav-tabs card-header-tabs flex-wrap">
              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/db/manage/${teamId}`}>
                  Manage
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/db/followup/${teamId}`}>
                  Follow Up
                </Link>
              </li>
              <SummaryNavItems tab={tab} onChange={setTab} />
              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/db/form/${teamId}`}>
                  Form
                </Link>
              </li>
            </ul>
          </div>

          <div className="card-body">
            {/* Gate loader */}
            {access.error ? (
            <ErrorState title="Unable to verify access" message={access.error} className="py-2" />
          ) : view === null ? (
            <SummaryBodySkeleton />
          ) : view === false ? (
              <div className="row justify-content-center mt-3">
                <div className="alert alert-danger text-center" role="alert">
                  You do not have permission to access this team M-Smart page
                </div>
              </div>
            ) : (
              <>
                {/* Sticky Filter Bar */}
                <div className="position-sticky" style={{ top: 74, zIndex: 5 }}>
                  <div className="card p-3 mb-3">
                    <div className="row g-2 align-items-end">
                      <div className="col-12 col-lg-8">
                        <div className="form-floating">
                          <Flatpickr
                            id="filterDate"
                            className="form-control shadow-none"
                            value={filterDate}
                            placeholder="Select date range"
                            options={{
                              dateFormat: "d-m-Y",
                              mode: "range",
                              disableMobile: true,
                            }}
                            onChange={handleDateChange}
                          />
                          <label htmlFor="filterDate">Date Range</label>
                        </div>
                        <div className="text-muted mt-1" style={{ fontSize: "0.85rem" }}>
                          {rangeLabel}
                        </div>
                        <RangePresets disabled={loading} onPick={setFilterDate} />
                      </div>

                      <div className="col-12 col-lg-4">
                        <div className="d-flex gap-2 justify-content-lg-end flex-wrap">
                          <button
                            data-bs-toggle="modal"
                            data-bs-target="#setSalesTarget"
                            className="btn btn-outline-dark w-100 w-lg-auto"
                            type="button"
                          >
                            <i className="bi bi-gear me-2"></i>
                            Set Targeted Sales
                          </button>

                          <button
                            className="btn btn-dark w-100 w-lg-auto"
                            type="button"
                            onClick={() => exportToExcel(summary, filterDate[0], filterDate[1])}
                            disabled={loading}
                          >
                            <i className="bi bi-download me-2"></i>
                            Export
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Loading state */}
                {loading ? (
                  <div className="mt-3"><SummaryBodySkeleton withDateBar={false} /></div>
                ) : (
                  <div className="container-fluid p-0">

                    {tab === "analysis" ? (
                      <SummaryCharts summary={summary} start={filterDate[0]} end={filterDate[1]} />
                    ) : (
                    <>
                    {/* KPI grid */}
                    <div className="row g-3 mb-3">
                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-cash-coin"
                          title="Targeted Sales"
                          value={money(targeted)}
                          onClick={() =>
                            openKpiModal({
                              title: "Targeted Sales",
                              subtitle: rangeLabel,
                              icon: "bi-cash-coin",
                              tone: "text-dark",
                              total: money(targeted),
                              extraRows: [{ label: "Avg / day", value: money(targeted / daysInRange) }],
                              countByDate: salesTargetByDate,
                              isMoney: true,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-cash-coin"
                          title="Actual Sales"
                          value={money(actual)}
                          tone={actualTone}
                          sub={targeted ? `Achieved: ${achieved}%` : null}
                          onClick={() =>
                            openKpiModal({
                              title: "Actual Sales",
                              subtitle: rangeLabel,
                              icon: "bi-cash-coin",
                              tone: actualTone,
                              total: money(actual),
                              extraRows: [
                                { label: "Achieved", value: `${achieved}%` },
                                { label: "Avg / day", value: money(actual / daysInRange) },
                              ],
                              countByDate: salesActualByDate,
                              isMoney: true,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-graph-up-arrow"
                          title="Sales Gap"
                          value={money(gap)}
                          tone={gapTone}
                          onClick={() =>
                            openKpiModal({
                              title: "Sales Gap",
                              subtitle: rangeLabel,
                              icon: "bi-graph-up-arrow",
                              tone: gapTone,
                              total: money(gap),
                              extraRows: [{ label: "Avg / day", value: money(gap / daysInRange) }],
                              countByDate: salesGapByDate,
                              isMoney: true,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-cloud-plus-fill"
                          title="Database Uploaded"
                          value={summary.totalLeadsAdded.total || 0}
                          onClick={() =>
                            openKpiModal({
                              title: "Database Uploaded",
                              subtitle: rangeLabel,
                              icon: "bi-cloud-plus-fill",
                              tone: "text-dark",
                              total: summary.totalLeadsAdded.total || 0,
                              extraRows: [
                                {
                                  label: "Avg / day",
                                  value: ((summary.totalLeadsAdded.total || 0) / daysInRange).toFixed(2),
                                },
                              ],
                              countByDate: summary.totalLeadsAdded.countByDate,
                              isMoney: false,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-easel2-fill"
                          title="Leads Presented"
                          value={summary.totalPresentations.total || 0}
                          onClick={() =>
                            openKpiModal({
                              title: "Leads Presented",
                              subtitle: rangeLabel,
                              icon: "bi-easel2-fill",
                              tone: "text-dark",
                              total: summary.totalPresentations.total || 0,
                              extraRows: [
                                {
                                  label: "Avg / day",
                                  value: ((summary.totalPresentations.total || 0) / daysInRange).toFixed(2),
                                },
                              ],
                              countByDate: summary.totalPresentations.countByDate,
                              isMoney: false,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-person"
                          title="Follow-ups Done"
                          value={summary.followUpScheduled.total || 0}
                          onClick={() =>
                            openKpiModal({
                              title: "Follow Up",
                              subtitle: rangeLabel,
                              icon: "bi-person",
                              tone: "text-dark",
                              total: summary.followUpScheduled.total || 0,
                              extraRows: [
                                {
                                  label: "Avg / day",
                                  value: ((summary.followUpScheduled.total || 0) / daysInRange).toFixed(2),
                                },
                              ],
                              countByDate: summary.followUpScheduled.countByDate,
                              isMoney: false,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-clipboard2-check"
                          title={summary?.finalStatus?.Repeat?.total ? `Closed (+${summary.finalStatus.Repeat.total} repeat)` : "Closed"}
                          value={summary.finalStatus?.Closed?.total || 0}
                          tone="text-success"
                          onClick={() =>
                            openKpiModal({
                              title: "Closed",
                              subtitle: rangeLabel,
                              icon: "bi-clipboard2-check",
                              tone: "text-success",
                              total: summary.finalStatus?.Closed?.total || 0,
                              extraRows: [
                                {
                                  label: "Avg / day",
                                  value: ((summary.finalStatus?.Closed?.total || 0) / daysInRange).toFixed(2),
                                },
                              ],
                              countByDate: summary.finalStatus?.Closed?.countByDate,
                              isMoney: false,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-clipboard2-plus"
                          title="Booked"
                          value={summary.finalStatus?.Booking?.total || 0}
                          onClick={() =>
                            openKpiModal({
                              title: "Booked",
                              subtitle: rangeLabel,
                              icon: "bi-clipboard2-plus",
                              tone: "text-dark",
                              total: summary.finalStatus?.Booking?.total || 0,
                              extraRows: [
                                {
                                  label: "Avg / day",
                                  value: ((summary.finalStatus?.Booking?.total || 0) / daysInRange).toFixed(2),
                                },
                              ],
                              countByDate: summary.finalStatus?.Booking?.countByDate,
                              isMoney: false,
                            })
                          }
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <KpiCard
                          icon="bi-clipboard2-x"
                          title="Rejected"
                          value={summary.finalStatus?.Rejected?.total || 0}
                          tone="text-danger"
                          onClick={() =>
                            openKpiModal({
                              title: "Rejected",
                              subtitle: rangeLabel,
                              icon: "bi-clipboard2-x",
                              tone: "text-danger",
                              total: summary.finalStatus?.Rejected?.total || 0,
                              extraRows: [
                                {
                                  label: "Avg / day",
                                  value: ((summary.finalStatus?.Rejected?.total || 0) / daysInRange).toFixed(2),
                                },
                              ],
                              countByDate: summary.finalStatus?.Rejected?.countByDate,
                              isMoney: false,
                            })
                          }
                        />
                      </div>
                    </div>

                    {/* Metrics panels */}
                    <div className="row g-3 mb-3">
                      <div className="col-12">
                        <SectionCard title="Follow Up Rates">
                          <MetricRow label="Follow Up ➝ Booking" value={summary.followUpToBookingRate.total || 0} suffix="%" />
                          <MetricRow label="Follow Up ➝ Closed" value={summary.followUpToClosedRate.total || 0} suffix="%" />
                          <MetricRow label="Follow Up ➝ Rejected" value={summary.followUpToRejectedRate.total || 0} suffix="%" />
                        </SectionCard>
                      </div>

                      <div className="col-12 col-md-6">
                        <SectionCard title="Average Follow Up">
                          <MetricRow label="Per Lead" value={summary.avgFollowUpPerLead.total || 0} />
                          <MetricRow label="Per Closed Lead" value={summary.followUpPerCloseRatio.total || 0} />
                        </SectionCard>
                      </div>

                      <div className="col-12 col-md-6">
                        <SectionCard title="Booking Rates">
                          <MetricRow label="Booking ➝ Closed" value={summary.bookingToClosedRate.total || 0} suffix="%" />
                          <MetricRow label="Booking ➝ Rejected" value={summary.bookingToRejectedRate.total || 0} suffix="%" />
                        </SectionCard>
                      </div>
                    </div>

                    {/* Summary by Date */}
                    <div className="row g-3">
                      <div className="col-12">
                        <SectionCard
                          title="Summary by Date"
                          right={<span className="badge ds-badge">{getDateRangeList(filterDate[0], filterDate[1]).length} days</span>}
                        >
                          <div
                            className="table-responsive mt-2"
                            style={{
                              fontSize: "0.85rem",
                              whiteSpace: "nowrap",
                              maxHeight: 360,
                              overflow: "auto",
                            }}
                          >
                            <table className="table table-bordered table-hover table-sm align-middle">
                              <thead className="table-light">
                                <tr>
                                  <th>Date</th>
                                  <th>Leads Added</th>
                                  <th>Presentation</th>
                                  <th>Follow-ups Done</th>
                                  <th>Closed</th>
                                  <th>Rejected</th>
                                  <th>Booking</th>
                                  <th>Targeted Sales</th>
                                  <th>Actual Sales</th>
                                  <th>Sales Gap</th>
                                </tr>
                              </thead>
                              <tbody>
                                {getDateRangeList(filterDate[0], filterDate[1]).map((dateStr) => {
                                  const leads = summary.totalLeadsAdded.countByDate?.[dateStr] || 0;
                                  const pres = summary.totalPresentations.countByDate?.[dateStr] || 0;
                                  const fu = summary.followUpScheduled.countByDate?.[dateStr] || 0;
                                  const closed = summary.finalStatus.Closed.countByDate?.[dateStr] || 0;
                                  const rej = summary.finalStatus.Rejected.countByDate?.[dateStr] || 0;
                                  const book = summary.finalStatus.Booking.countByDate?.[dateStr] || 0;

                                  const targetAmount = summary.salesGapSummary.countByDate?.[dateStr]?.targetAmount || 0;
                                  const actualSales = summary.salesGapSummary.countByDate?.[dateStr]?.actualSalesAmount || 0;
                                  const salesGapRow = (summary.salesGapSummary.countByDate?.[dateStr]?.salesGap || 0) * -1;

                                  return (
                                    <tr key={dateStr}>
                                      <td className="fw-semibold">{dateStr}</td>
                                      <td className={leads ? "" : "text-danger"}>{leads}</td>
                                      <td className={pres ? "" : "text-danger"}>{pres}</td>
                                      <td className={fu ? "" : "text-danger"}>{fu}</td>
                                      <td className={closed ? "text-success fw-bold" : "text-danger"}>{closed}</td>
                                      <td className={rej ? "text-danger fw-bold" : "text-danger"}>{rej}</td>
                                      <td className={book ? "fw-bold" : "text-danger"}>{book}</td>

                                      <td className={targetAmount === 0 ? "text-danger" : ""}>{money(targetAmount)}</td>
                                      <td className={actualSales >= targetAmount ? "text-success" : "text-danger"}>{money(actualSales)}</td>
                                      <td className={salesGapRow < 0 ? "text-danger" : salesGapRow > 0 ? "text-success" : ""}>
                                        {money(salesGapRow)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </SectionCard>
                      </div>
                    </div>
                    </>
                    )}
                  </div>
                )}

                {/* KPI Detail Modal */}
                <div className={`modal fade ${kpiModal.open ? "show d-block" : ""}`} tabIndex="-1" role="dialog">
                  <div className="modal-dialog modal-dialog-centered ss-modal-dialog" role="document">
                    <div className="modal-content ss-modal">
                      <div className="modal-header ss-modal-header">
                        <div className="d-flex align-items-center gap-2">
                          <div className="ss-kpi-icon" style={{ width: 40, height: 40 }}>
                            <i className={`bi ${kpiModal.icon}`} />
                          </div>
                          <div>
                            <div className="modal-title">{kpiModal.title}</div>
                            <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                              {kpiModal.subtitle}
                            </div>
                          </div>
                        </div>
                        <button type="button" className="btn-close" onClick={closeKpiModal} />
                      </div>

                      <div className="modal-body ss-modal-body">
                        <div className="ss-kpi-total">
                          <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                            Total
                          </div>
                          <div className={`fw-bold ${kpiModal.tone}`} style={{ fontSize: "1.4rem" }}>
                            {kpiModal.total}
                          </div>
                        </div>

                        {kpiModal.extraRows?.length ? (
                          <div className="mt-3">
                            {kpiModal.extraRows.map((r, idx) => (
                              <div key={idx} className="d-flex justify-content-between py-2 ss-kpi-row">
                                <div className="text-muted">{r.label}</div>
                                <div className="fw-bold">{r.value}</div>
                              </div>
                            ))}
                          </div>
                        ) : null}

                        {kpiModal.countByDate ? (
                          <div className="mt-3">
                            <div className="fw-bold mb-2">Top Days</div>
                            {calcTopDays(kpiModal.countByDate, 3).length ? (
                              <div className="ss-kpi-topdays">
                                {calcTopDays(kpiModal.countByDate, 3).map(([d, v]) => (
                                  <div key={d} className="d-flex justify-content-between py-2 ss-kpi-row">
                                    <div className="text-muted">{d}</div>
                                    <div className="fw-bold">{kpiModal.isMoney ? money(v) : v}</div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                                No activity in this range.
                              </div>
                            )}
                          </div>
                        ) : null}

                        <div className="d-grid mt-3">
                          <button className="btn btn-dark" onClick={closeKpiModal}>
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {kpiModal.open ? <div className="modal-backdrop fade show" onClick={closeKpiModal} /> : null}

                {/* Sales Target Modal */}
                <div className="modal fade" id="setSalesTarget" tabIndex="-1" aria-labelledby="setSalesTarget" aria-hidden="true">
                  <div className="modal-dialog modal-dialog-centered ss-modal-dialog">
                    <div className="modal-content ss-modal">
                      <div className="modal-header ss-modal-header">
                        <h5 className="modal-title" id="setSalesTarget">
                          Set Sales Target
                        </h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                      </div>
                      <div className="modal-body ss-modal-body text-start">
                        <SalesTartget teamId={teamId} />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
